import { FC } from "react";
import TableComponent from "../../../../../common/components/table/table.component";
import DispatcherListingItem from "../common/types/dispatcher-listing-item";
import useDispatcherListingUserPermissions from "../common/user-permissions/use-dispatcher-listing-user-permissions";
import DispatcherListingTableColumn from "../common/types/dispatcher-listing-table-column";
import dispatcherListingTableHelper from "./dispatcher-listing-table.helper";
import DispatcherListingTableRow from "../common/types/dispatcher-listing-table-row";
import TableLinkButtonComponent from "../../../../../common/components/table/button/link/table-link-button.component";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import userRoutesHelper from "../../../common/routes/user-routes.helper";
import userTranslationsHelper from "../../../../../languages/user-translations.helper";
import TableDeleteButtonComponent from "../../../../../common/components/table/button/delete/table-delete-button.component";
import UserStatus from "../../../../../common/types/user-status";
import UserDeleteUserData from "../../../common/delete/common/types/user-delete-user-data";

type DispatcherListingTableProps = {
  isError: boolean;
  isLoading: boolean;
  listingItems: DispatcherListingItem[];
  onSelectUserToDelete: (userData: UserDeleteUserData) => void;
};

const DispatcherListingTableComponent: FC<DispatcherListingTableProps> = (
  props
) => {
  const userPermissions = useDispatcherListingUserPermissions();

  const columns: DispatcherListingTableColumn[] =
    dispatcherListingTableHelper.getColumns();
  const translations =
    userTranslationsHelper.getDispatcherListingTranslations();

  const createTableRow = (
    listingItem: DispatcherListingItem
  ): DispatcherListingTableRow => {
    const isEditButtonVisible =
      userPermissions.hasAccessToEditUser &&
      listingItem.status !== UserStatus.DELETED;
    const isDeleteButtonVisible =
      userPermissions.hasAccessToDeleteUser &&
      listingItem.status !== UserStatus.DELETED;
    const activityStatus = dispatcherListingTableHelper.getStatusText(
      listingItem.status
    );

    return {
      id: listingItem.uuid,
      value: {
        cargoCompany: (
          <div title={listingItem.cargoCompanyName}>
            {listingItem.cargoCompanyName}
          </div>
        ),
        dispatch: (
          <div title={listingItem.dispatch}>{listingItem.dispatch}</div>
        ),
        firstName: (
          <div title={listingItem.firstName}>{listingItem.firstName}</div>
        ),
        lastName: (
          <div title={listingItem.lastName}>{listingItem.lastName}</div>
        ),
        username: (
          <div title={listingItem.username}>{listingItem.username}</div>
        ),
        email: <div title={listingItem.email}>{listingItem.email}</div>,
        phoneNumbers: (
          <div>
            <div>
              {listingItem.phoneNumbers.map((phoneNumber, index) => (
                <div key={index} title={phoneNumber}>
                  {phoneNumber}
                </div>
              ))}
            </div>
          </div>
        ),
        status: <div title={activityStatus}>{activityStatus}</div>,
        actions: (
          <div className="d-flex">
            {isEditButtonVisible && (
              <TableLinkButtonComponent
                icon={faEdit}
                to={userRoutesHelper.getDispatcherEditRoute({
                  dispatcherUuid: listingItem.uuid,
                })}
                title={translations.table.dispatcherEditButtonTitle}
                idForTesting={`dispatcher-listing-table-item-${listingItem.uuid}-edit-button`}
              />
            )}
            {isDeleteButtonVisible && (
              <TableDeleteButtonComponent
                onClick={() =>
                  props.onSelectUserToDelete({
                    uuid: listingItem.uuid,
                    firstName: listingItem.firstName,
                    lastName: listingItem.lastName,
                  })
                }
                title={translations.table.dispatcherDeleteButtonTitle}
                idForTesting={`dispatcher-listing-table-item-${listingItem.uuid}-delete-button`}
              />
            )}
          </div>
        ),
      },
    };
  };

  const rows: DispatcherListingTableRow[] = props.listingItems.map(
    (listingItem) => createTableRow(listingItem)
  );

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isError={props.isError}
      isLoading={props.isLoading}
    />
  );
};

export default DispatcherListingTableComponent;
